'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons'
import '../../sass/landing/PhoneMockup.scss'

type AllergenKey = 'gluten' | 'lacteo' | 'pescado' | 'huevos' | 'frutos con cáscara'

interface MockupAllergen {
  key: AllergenKey
  color: string
}

interface MockupItem {
  id: string
  allergens: MockupAllergen[]
  prices: string[]
  image: string
}

const items: MockupItem[] = [
  {
    id: 'bravas',
    allergens: [
      { key: 'gluten', color: '#E8843B' },
      { key: 'lacteo', color: '#A8B0C0' }
    ],
    prices: ['6.50€'],
    image: '/img/landing/food/bravas.jpg'
  },
  {
    id: 'quesos',
    allergens: [
      { key: 'lacteo', color: '#A8B0C0' },
      { key: 'frutos con cáscara', color: '#E8843B' }
    ],
    prices: ['19.50€'],
    image: '/img/landing/food/quesos.jpg'
  },
  {
    id: 'crema',
    allergens: [
      { key: 'lacteo', color: '#A8B0C0' }
    ],
    prices: ['7.00€'],
    image: '/img/landing/food/crema.jpg'
  },
  {
    id: 'tacos',
    allergens: [
      { key: 'gluten', color: '#E8843B' },
      { key: 'lacteo', color: '#A8B0C0' }
    ],
    prices: ['9.50€'],
    image: '/img/landing/food/tacos.jpg'
  },
  {
    id: 'bowl',
    allergens: [
      { key: 'frutos con cáscara', color: '#E8843B' }
    ],
    prices: ['11.00€'],
    image: '/img/landing/food/bowl.jpg'
  },
  {
    id: 'hamburguesa',
    allergens: [
      { key: 'gluten', color: '#E8843B' },
      { key: 'lacteo', color: '#A8B0C0' }
    ],
    prices: ['13.50€'],
    image: '/img/landing/food/hamburguesa.jpg'
  },
  {
    id: 'pasta',
    allergens: [
      { key: 'gluten', color: '#E8843B' },
      { key: 'lacteo', color: '#A8B0C0' }
    ],
    prices: ['10.50€'],
    image: '/img/landing/food/pasta.jpg'
  },
  {
    id: 'tostada',
    allergens: [
      { key: 'gluten', color: '#E8843B' }
    ],
    prices: ['8.00€'],
    image: '/img/landing/food/tostada.jpg'
  }
]

interface PhoneMockupProps {
  showAllergens?: boolean
}

const PhoneMockup = ({ showAllergens = true }: PhoneMockupProps) => {
  const t = useTranslations('LandingV2.hero')

  return (
    <Link href='/login/signup' className='phone-mockup-link' aria-label={t('mockupCta')}>
      <div className='phone-mockup' aria-label='Vista previa del menú digital de Barcraft'>
        <div className='phone-mockup__notch' aria-hidden='true' />
        <div className='phone-mockup__side phone-mockup__side--left' aria-hidden='true'>
          <span /><span /><span />
        </div>
        <div className='phone-mockup__side phone-mockup__side--right' aria-hidden='true'>
          <span /><span />
        </div>

        <div className='phone-mockup__screen'>
          <div className='phone-mockup__header'>
            <div className='phone-mockup__socials' aria-hidden='true'>
              <span className='phone-mockup__social' onClick={(e) => e.preventDefault()}>
                <FontAwesomeIcon icon={faInstagram} />
              </span>
              <span className='phone-mockup__social' onClick={(e) => e.preventDefault()}>
                <FontAwesomeIcon icon={faFacebook} />
              </span>
            </div>
            <h2 className='phone-mockup__title'>{t('mockupTitle')}</h2>
          </div>

          <div className='phone-mockup__content'>
            <div className='phone-mockup__menu'>
              <div className='phone-mockup__section-title'>{t('sectionTitle')}</div>

              <div className='phone-mockup__items'>
                {items.map((item) => (
                  <article key={item.id} className='phone-mockup__item'>
                    <div className='phone-mockup__item-info'>
                      <h3 className='phone-mockup__item-name'>{t(`mockupItems.${item.id}.name`)}</h3>
                      <p className='phone-mockup__item-desc'>{t(`mockupItems.${item.id}.description`)}</p>
                      {showAllergens && (
                        <div className='phone-mockup__item-allergens'>
                          {item.allergens.map((a) => (
                            <span key={`${item.id}-${a.key}`} className='phone-mockup__allergen'>
                              <Image
                                src={`/img/allergens/${a.key}.svg`}
                                alt={a.key}
                                width={18}
                                height={18}
                              />
                            </span>
                          ))}
                        </div>
                      )}
                      {item.prices.length > 0 && (
                        <div className='phone-mockup__item-prices'>
                          {item.prices.map((p) => (
                            <span key={p} className='phone-mockup__price'>
                              <span className='phone-mockup__price-text'>{p}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className='phone-mockup__item-photo'>
                      <Image
                        src={item.image}
                        alt={t(`mockupItems.${item.id}.name`)}
                        width={80}
                        height={80}
                      />
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}

export default PhoneMockup
